import { useEffect, useMemo, useRef } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { PageHeader } from '../ui/PageHeader';
import { Card, Grid, Stack } from '../ui/Card';
import { DataTable, type Column } from '../ui/DataTable';
import { EvidenceMark } from '../ui/EvidenceMark';
import { Badge } from '../ui/Badge';
import { EmptyState } from '../ui/EmptyState';
import { DataGate } from './parts';
import { Skeleton, SkeletonKpi, SkeletonTable, SkeletonText } from '../ui';
import { usePageTrail } from '../shell/ShellContext';
import { usePersona } from '../auth/RoleContext';
import { useTrackData } from '../data/useTrackData';
import type { DataState } from '../data/useTrackData';
import { FILES } from '../data/loader';
import {
  cycleTitle,
  dateShort,
  ha,
  placementUnknown,
} from '../lib/format';
import {
  FOLLOWUP_RESULT_LABEL,
  ORIGIN_BADGE_LABEL,
  ORIGIN_DETAIL_LABEL,
  ORIGIN_ICON,
  contourExtraWorks,
  requirementLine,
  useExtraWorksBothCycles,
  type ExtraWork,
} from './my/extraWorks';
import type {
  ContourMeta,
  Crop,
  Cycle,
  Farm,
  Massif,
  Obligation,
  QuarantineFile,
  Work,
} from '../data/types';
import { useRouteSource } from '../data-source/provider';
import { defineMessages } from '../../i18n/define';
import { useT } from '../../i18n/hooks';
import { tr } from '../../i18n/runtime';
import { ContourCabinet } from './cabinet/ContourCabinet';
import {
  contourPurpose,
  ContourKpis,
  ContourMapCard,
  ContourWorksCard,
  ContourWorksTable,
  LandUseCard,
  QuarantineFacts,
  type ContourWorkLine,
} from './contourParts';
import './pages.css';

const M = defineMessages({
  catArableIrr: { id: 'contour.detail.category.arableIrr', defaultMessage: 'пашня орошаемая', description: 'Категория земли контура в KPI «Категория» старого паспорта: орошаемая пашня' },
  catArableRain: { id: 'contour.detail.category.arableRain', defaultMessage: 'пашня богарная', description: 'Категория земли контура: богарная (неорошаемая) пашня' },
  catOrchard: { id: 'contour.detail.category.orchard', defaultMessage: 'сад', description: 'Категория земли контура: сад' },
  catVineyard: { id: 'contour.detail.category.vineyard', defaultMessage: 'виноградник', description: 'Категория земли контура: виноградник' },
  catPasture: { id: 'contour.detail.category.pasture', defaultMessage: 'пастбище', description: 'Категория земли контура: пастбище' },
  catOther: { id: 'contour.detail.category.other', defaultMessage: 'прочее', description: 'Категория земли контура: прочее' },
  stateVerified: { id: 'contour.detail.state.verified', defaultMessage: 'отмечено проверяющим', description: 'Состояние работы на контуре (бейдж в таблице работ старого паспорта): отчёт подтверждён проверяющим; коротко' },
  stateReported: { id: 'contour.detail.state.reported', defaultMessage: 'отчитались', description: 'Состояние работы на контуре (бейдж): хозяйство отчиталось; коротко' },
  stateInWindow: { id: 'contour.detail.state.inWindow', defaultMessage: 'окно идёт', description: 'Состояние работы на контуре (бейдж): окно работы идёт; коротко' },
  stateUpcoming: { id: 'contour.detail.state.upcoming', defaultMessage: 'впереди', description: 'Состояние работы на контуре (бейдж): окно впереди; коротко' },
  stateSilent: { id: 'contour.detail.state.silent', defaultMessage: 'без отчёта', description: 'Состояние работы на контуре (бейдж): отчёта нет; коротко' },
  wheatLabel: { id: 'contour.detail.cycle.wheat', defaultMessage: 'Пшеница 2025–2026', description: 'Название агроцикла озимой пшеницы 2025–26, пока список агроциклов не загружен' },
  cottonLabel: { id: 'contour.detail.cycle.cotton', defaultMessage: 'Хлопок 2026', description: 'Название агроцикла хлопка 2026, пока список агроциклов не загружен' },
  trailDistrict: { id: 'contour.detail.trail.district', defaultMessage: 'Район', description: 'Уровень в цепочке навигации (хлебные крошки): район' },
  trailDistrictName: { id: 'contour.detail.trail.districtName', defaultMessage: 'Бука', description: 'Название района (Бука) в цепочке навигации; имя собственное' },
  trailMassif: { id: 'contour.detail.trail.massif', defaultMessage: 'Массив', description: 'Уровень в цепочке навигации: массив' },
  trailFarm: { id: 'contour.detail.trail.farm', defaultMessage: 'Хозяйство', description: 'Уровень в цепочке навигации: хозяйство' },
  trailContour: { id: 'contour.detail.trail.contour', defaultMessage: 'Контур', description: 'Уровень в цепочке навигации: контур' },
  contourNo: { id: 'contour.detail.contourNo', defaultMessage: '№ {no}', description: 'Номер контура в цепочке навигации; {no} — кадастровый номер' },
  titleId: { id: 'contour.detail.title.id', defaultMessage: 'Контур {id}', description: 'Заголовок страницы, когда контур не найден; {id} — адрес из ссылки' },
  titleNo: { id: 'contour.detail.title.no', defaultMessage: 'Контур № {no}', description: 'Заголовок страницы контура; {no} — кадастровый номер' },
  colDate: { id: 'contour.detail.extra.col.date', defaultMessage: 'Дата', description: 'Колонка таблицы добровольных работ контура: дата; узкая колонка' },
  colCycle: { id: 'contour.detail.extra.col.cycle', defaultMessage: 'Агроцикл', description: 'Колонка таблицы добровольных работ контура: агроцикл; узкая колонка' },
  colWork: { id: 'contour.detail.extra.col.work', defaultMessage: 'Работа', description: 'Колонка таблицы добровольных работ контура: название работы' },
  colOrigin: { id: 'contour.detail.extra.col.origin', defaultMessage: 'Происхождение', description: 'Колонка таблицы добровольных работ контура: откуда взялась запись (вручную, по предписанию…)' },
  notFoundTitle: { id: 'contour.detail.notFound.title', defaultMessage: 'Контура с таким адресом в срезе нет', description: 'Заголовок пустого состояния: контур по ссылке не найден' },
  notFoundReason: { id: 'contour.detail.notFound.reason', defaultMessage: 'Реестр контуров прочитан, но контура «{id}» в нём не оказалось. Проверьте адрес или переключите трек данных: паспорт, работы, карантин и карта здесь не показываются — показывать про несуществующий контур нечего.', description: 'Пояснение к пустому состоянию «контур не найден»; {id} — адрес из ссылки; «трек данных» — переключатель источника данных' },
  foreignTitle: { id: 'contour.detail.foreign.title', defaultMessage: 'Этот контур не относится к вашему хозяйству', description: 'Заголовок пустого состояния для фермера: контур чужого хозяйства' },
  foreignReason: { id: 'contour.detail.foreign.reason', defaultMessage: 'Кабинет хозяйства показывает только свои контуры: № {no} числится за другим хозяйством, и его паспорт, работы и карантин здесь не показываются. Это граница уровня просмотра, а не потеря данных.', description: 'Пояснение к пустому состоянию для фермера; {no} — номер контура' },
  foreignBack: { id: 'contour.detail.foreign.back', defaultMessage: 'Вернуться к своим полям', description: 'Кнопка-ссылка в пустом состоянии для фермера: перейти к списку своих полей' },
  purposeMissing: { id: 'contour.detail.purposeMissing', defaultMessage: 'Контур в срезе не найден — проверьте адрес или трек данных.', description: 'Описание под заголовком страницы, когда контура нет в срезе' },
  noGeometry: { id: 'contour.detail.geometry.none', defaultMessage: 'Геометрии нет', description: 'Бейдж в шапке контура: геометрии нет; коротко' },
  geometryCandidate: { id: 'contour.detail.geometry.candidate', defaultMessage: 'Кандидат — не подтверждено', description: 'Метка в шапке контура: геометрия — кандидат, не подтверждена' },
  geometryAccepted: { id: 'contour.detail.geometry.accepted', defaultMessage: 'Принята (производная привязка NGIS)', description: 'Метка в шапке контура: геометрия принята; NGIS — название источника привязки' },
  gatePassport: { id: 'contour.detail.gate.passport', defaultMessage: 'паспорт контура не получен', description: 'Что не пришло (подпись пустого состояния данных): паспорт контура' },
  skelPassport: { id: 'contour.detail.skeleton.passport', defaultMessage: 'паспорт контура', description: 'Что загружается (подпись скелетона KPI для чтения с экрана), после слова «Загружаю»' },
  kpiAreaNote: { id: 'contour.detail.kpi.areaNote', defaultMessage: 'по реестру среза', description: 'Мелкая подпись под KPI «Площадь» старого паспорта контура' },
  kpiCategory: { id: 'contour.detail.kpi.category', defaultMessage: 'Категория', description: 'Подпись KPI старого паспорта: категория земли контура' },
  kpiCategoryNote: { id: 'contour.detail.kpi.categoryNote', defaultMessage: 'землепользование контура', description: 'Мелкая подпись под KPI «Категория»' },
  kpiCrop: { id: 'contour.detail.kpi.crop', defaultMessage: 'Культура 2026', description: 'Подпись KPI старого паспорта: культура контура в сезоне 2026' },
  kpiCropNote: { id: 'contour.detail.kpi.cropNote', defaultMessage: 'размещение текущего сезона', description: 'Мелкая подпись под KPI «Культура 2026»' },
  kpiCropMissing: { id: 'contour.detail.kpi.cropMissing', defaultMessage: 'культура на 2026 не проставлена — это пробел данных, а не пар', description: 'Мелкая подпись под KPI «Культура 2026», когда размещения нет; «пар» — земля под парами' },
  kpiWinter: { id: 'contour.detail.kpi.winter', defaultMessage: 'Озимая 2025', description: 'Подпись KPI старого паспорта: озимая культура сезона 2025' },
  kpiWinterNone: { id: 'contour.detail.kpi.winterNone', defaultMessage: 'не было', description: 'Значение KPI «Озимая 2025», когда озимых на контуре не было' },
  kpiWinterNote: { id: 'contour.detail.kpi.winterNote', defaultMessage: 'озимый клин 2025–26', description: 'Мелкая подпись под KPI «Озимая 2025»: озимый клин (посевы озимых) сезона 2025–26' },
  landUseCaption: { id: 'contour.detail.landUse.caption', defaultMessage: 'Период не зафиксирован — реестр не датирует начало.', description: 'Подпись под строкой землепользования в старом паспорте контура' },
  skelWorks: { id: 'contour.detail.skeleton.works', defaultMessage: 'работы по контуру', description: 'Что загружается (подпись скелетона таблицы работ для чтения с экрана)' },
  worksMissingTitle: { id: 'contour.detail.works.missing.title', defaultMessage: 'Работы не загружены', description: 'Заголовок пустого состояния таблицы работ контура: данные не пришли' },
  worksMissingReason: { id: 'contour.detail.works.missing.reason', defaultMessage: 'данные работ для обоих агроциклов не найдены в срезе', description: 'Пояснение: данные работ обоих агроциклов отсутствуют в срезе' },
  worksEmptyReason: { id: 'contour.detail.works.empty.reason', defaultMessage: 'ни один агроцикл не назначил работу этому контуру в текущем срезе', description: 'Пояснение к пустой таблице работ контура' },
  gateExtra: { id: 'contour.detail.gate.extra', defaultMessage: 'добровольные работы контура не получены', description: 'Что не пришло (подпись пустого состояния данных): добровольные работы контура' },
  skelExtra: { id: 'contour.detail.skeleton.extra', defaultMessage: 'добровольные работы', description: 'Что загружается (подпись скелетона таблицы для чтения с экрана)' },
  extraUnit: { id: 'contour.detail.extra.denominator', defaultMessage: '{count, plural, one {строка} few {строки} many {строк} other {строки}}', description: 'Слово-знаменатель под таблицей добровольных работ: «5 из 12 строк»; без числа' },
  extraEmptyTitle: { id: 'contour.detail.extra.empty.title', defaultMessage: 'Добровольных работ нет', description: 'Заголовок пустой таблицы добровольных работ контура' },
  extraEmptyReason: { id: 'contour.detail.extra.empty.reason', defaultMessage: 'добровольных записей на этом контуре в срезе нет', description: 'Пояснение к пустой таблице добровольных работ контура' },
  quarantineTitle: { id: 'contour.detail.quarantine.title', defaultMessage: 'Карантин', description: 'Заголовок карточки карантина в старом паспорте контура' },
  gateQuarantine: { id: 'contour.detail.gate.quarantine', defaultMessage: 'статус карантина не получен', description: 'Что не пришло (подпись пустого состояния данных): статус карантина' },
  redWithCause: { id: 'contour.detail.quarantine.redCause', defaultMessage: '<b>Красный с {date}.</b> Причина: {cause}.', description: 'Строка карантина: красный статус; {date} — с какой даты, {cause} — причина; тег b — жирный' },
  redNoCause: { id: 'contour.detail.quarantine.redNoCause', defaultMessage: '<b>Красный с {date}.</b> Причина не названа.', description: 'Строка карантина: красный статус без причины; {date} — с какой даты; тег b — жирный' },
  watchWithCause: { id: 'contour.detail.quarantine.watchCause', defaultMessage: 'Под наблюдением с {date}. Причина: {cause}.', description: 'Строка карантина: жёлтый статус (наблюдение); {date} — с какой даты, {cause} — причина' },
  watchNoCause: { id: 'contour.detail.quarantine.watchNoCause', defaultMessage: 'Под наблюдением с {date}. Причина не названа.', description: 'Строка карантина: жёлтый статус без причины; {date} — с какой даты' },
  greenRecorded: { id: 'contour.detail.quarantine.green', defaultMessage: 'Зелёный с {date}: отдельная запись подтверждена в срезе.', description: 'Строка карантина: зелёный статус, есть запись; {date} — с какой даты' },
  greenDefault: { id: 'contour.detail.quarantine.greenDefault', defaultMessage: 'Зелёный — по умолчанию: событий не было. Отдельной записи по этому контуру в срезе нет — это оговорка о данных, а не результат обследования.', description: 'Строка карантина: зелёный статус по умолчанию, записей нет' },
  inspClean: { id: 'contour.detail.quarantine.inspClean', defaultMessage: 'без находок', description: 'Строка обследования контура: ничего не найдено' },
  inspFinding: { id: 'contour.detail.quarantine.inspFinding', defaultMessage: '{kind}: {name}', description: 'Строка обследования контура с находкой; {kind} — вид («вредитель»), {name} — название' },
  inspFindingMeasure: { id: 'contour.detail.quarantine.inspFindingMeasure', defaultMessage: '{kind}: {name} — {measure}', description: 'Строка обследования контура с находкой и мерой; {kind} — вид, {name} — название, {measure} — размер поражения' },
  inspKindFallback: { id: 'contour.detail.quarantine.kindFallback', defaultMessage: 'находка', description: 'Подстановка вместо вида находки, когда он не назван' },
  reqIssued: { id: 'contour.detail.quarantine.reqIssued', defaultMessage: 'выдано предписание, срок исполнения — {deadline}.', description: 'Строка ленты карантина: выдано предписание; {deadline} — срок, короткая дата' },
  reqIssuedMeasure: { id: 'contour.detail.quarantine.reqIssuedMeasure', defaultMessage: 'выдано предписание, срок исполнения — {deadline}. Мера: {measure}', description: 'Строка ленты карантина: выдано предписание с мерой; {deadline} — срок, {measure} — что нужно сделать' },
  followupLine: { id: 'contour.detail.quarantine.followup', defaultMessage: 'повторный контроль: {result}', description: 'Строка ленты карантина: итог повторного контроля; {result} — исход' },
  noFollowup: { id: 'contour.detail.quarantine.noFollowup', defaultMessage: 'Повторного контроля по этому предписанию в срезе нет — это отсутствие записи, а не подтверждение, что очаг сохранился.', description: 'Пояснение под лентой карантина: повторных проверок нет' },
});

const CATEGORY_MSG = {
  arable_irr: M.catArableIrr,
  arable_rain: M.catArableRain,
  orchard: M.catOrchard,
  vineyard: M.catVineyard,
  pasture: M.catPasture,
  other: M.catOther,
} as const;

const STATE_MSG = {
  reported_verified: M.stateVerified,
  reported: M.stateReported,
  in_window: M.stateInWindow,
  upcoming: M.stateUpcoming,
  silent: M.stateSilent,
} as const satisfies Record<Obligation['state'], unknown>;

/**
 * Прототип живёт ровно двумя циклами (см. DESIGN-BRIEF.md «Мир данных»):
 * пшеница 2025–26 закрыта, хлопок 2026 идёт. ID стабильны на обоих треках.
 */
const CYCLE_WHEAT = 'cycle-wheat-2025';
const CYCLE_COTTON = 'cycle-cotton-2026';

function buildContourWorkRows(
  obligations: Obligation[] | null,
  works: Work[] | null,
  cycleLabel: string,
  contourId: string,
): ContourWorkLine[] {
  if (!obligations || !works) return [];
  const workById = new Map(works.map((w) => [w.id, w]));
  return obligations
    .filter((o) => o.contour_id === contourId)
    .map((o) => {
      const work = workById.get(o.work_id);
      return {
        key: o.id,
        work: work?.name ?? o.work_id,
        cycle: cycleLabel,
        windowFrom: work?.window.from || null,
        windowTo: work?.window.to || null,
        state: { text: tr(STATE_MSG[o.state]), quiet: o.state === 'silent' },
      };
    });
}

function anyLoading(...resources: { state: DataState }[]): boolean {
  return resources.some((r) => r.state === 'loading');
}

function allMissing(...resources: { state: DataState }[]): boolean {
  return resources.every((r) => r.state === 'missing');
}

/**
 * Карточка контура выбирает реализацию по источнику маршрута: на API —
 * карточка контура с историей (`cabinet/ContourCabinet`, id канона), иначе — фикстурный паспорт прототипа.
 * Id двух миров не пересекаются, поэтому смешивать их в одном компоненте
 * нельзя: ссылка реестра на каноне вела бы в «нет в срезе».
 */
export function ContourDetail() {
  const route = useRouteSource();
  return route?.source === 'api' ? <ContourCabinet /> : <ContourDetailFixtures />;
}

export function ContourDetailFixtures() {
  const t = useT();
  const { id = '' } = useParams();
  const { persona } = usePersona();
  const contours = useTrackData<ContourMeta[]>(FILES.contours);
  const crops = useTrackData<Crop[]>(FILES.crops);
  const quarantine = useTrackData<QuarantineFile>(FILES.quarantine);
  const massifs = useTrackData<Massif[]>(FILES.massifs);
  const farms = useTrackData<Farm[]>(FILES.farms);
  const cycles = useTrackData<Cycle[]>(FILES.cycles);
  const worksWheat = useTrackData<Work[]>(FILES.works(CYCLE_WHEAT));
  const worksCotton = useTrackData<Work[]>(FILES.works(CYCLE_COTTON));
  const obligWheat = useTrackData<Obligation[]>(FILES.obligations(CYCLE_WHEAT));
  const obligCotton = useTrackData<Obligation[]>(FILES.obligations(CYCLE_COTTON));
  const extraBundle = useExtraWorksBothCycles();

  const contour = (contours.data ?? []).find((c) => c.id === id) ?? null;
  const status = (quarantine.data?.statuses ?? []).find((s) => s.contour_id === id) ?? null;
  const inspections = useMemo(
    () => (quarantine.data?.inspections ?? []).filter((i) => i.contour_id === id),
    [quarantine.data, id],
  );
  /** Требование и его повторный замер — те же две записи ленты, что и обследования. */
  const requirement = status?.requirement ?? null;
  const followup = status?.followup ?? null;

  /**
   * Приход по якорю извне (строка «по предписанию» на `/my/history` ведёт на
   * `/contours/:id#quarantine`). Роутер сам к хэшу не доезжает, а доскроллить
   * по первому появлению блока мало: страница дочитывает обязательства обоих
   * циклов и реестр контуров, растёт на полторы тысячи пикселей, и якорь
   * уезжает вниз уже после прокрутки. Поэтому прокрутка делается один раз —
   * когда все ресурсы экрана перестали грузиться и высота уже не меняется.
   */
  const location = useLocation();
  const jumped = useRef(false);
  const pageSettled = !anyLoading(
    contours,
    crops,
    quarantine,
    massifs,
    farms,
    cycles,
    worksWheat,
    worksCotton,
    obligWheat,
    obligCotton,
    extraBundle,
  );
  useEffect(() => {
    if (jumped.current || !location.hash || !pageSettled) return;
    const node = document.getElementById(location.hash.slice(1));
    if (!node) return;
    jumped.current = true;
    node.scrollIntoView({ block: 'start' });
  }, [location.hash, pageSettled]);

  const massifName = contour ? massifs.data?.find((m) => m.id === contour.massif_id)?.name ?? contour.massif_id : null;
  const farmName = contour ? farms.data?.find((f) => f.id === contour.farm_id)?.name ?? contour.farm_id : null;

  const cropNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of crops.data ?? []) map.set(c.id, c.name);
    return map;
  }, [crops.data]);
  const cropName = (cropId: string | null) =>
    cropId ? cropNameById.get(cropId) ?? cropId : placementUnknown();

  usePageTrail([
    { level: t(M.trailDistrict), name: t(M.trailDistrictName), to: '/' },
    {
      level: t(M.trailMassif),
      name: massifName ?? '—',
      // Массив — фильтр реестра контуров, не страница массива (№85).
      to: contour ? `/contours?massif=${contour.massif_id}` : undefined,
    },
    {
      level: t(M.trailFarm),
      name: farmName ?? '—',
      to: contour ? `/farms/${contour.farm_id}` : undefined,
    },
    { level: t(M.trailContour), name: contour ? t(M.contourNo, { no: contour.no }) : id },
  ]);

  const wheatCycle = cycles.data?.find((c) => c.id === CYCLE_WHEAT);
  const cottonCycle = cycles.data?.find((c) => c.id === CYCLE_COTTON);
  const wheatLabel = wheatCycle ? cycleTitle(wheatCycle, crops.data) : t(M.wheatLabel);
  const cottonLabel = cottonCycle ? cycleTitle(cottonCycle, crops.data) : t(M.cottonLabel);

  const workRows = useMemo(() => {
    const wheatRows = buildContourWorkRows(obligWheat.data, worksWheat.data, wheatLabel, id);
    const cottonRows = buildContourWorkRows(obligCotton.data, worksCotton.data, cottonLabel, id);
    return [...wheatRows, ...cottonRows];
  }, [obligWheat.data, worksWheat.data, obligCotton.data, worksCotton.data, wheatLabel, cottonLabel, id]);

  const worksLoading = anyLoading(obligWheat, obligCotton, worksWheat, worksCotton);
  const worksAllMissing = allMissing(obligWheat, obligCotton, worksWheat, worksCotton);

  /** Та же пара подписей цикла, что и у шаблонных строк выше — доп. работа знает только cycle_id. */
  const extraCycleLabel = (cycleId: string): string =>
    cycleId === CYCLE_WHEAT ? wheatLabel : cycleId === CYCLE_COTTON ? cottonLabel : cycleId;

  const extraWorkColumns: Column<ExtraWork>[] = [
    { key: 'date', header: t(M.colDate), width: '130px', render: (row) => dateShort(row.date) },
    { key: 'cycle', header: t(M.colCycle), render: (row) => extraCycleLabel(row.cycle_id) },
    {
      key: 'name',
      header: t(M.colWork),
      render: (row) => {
        const requirement = requirementLine(row);
        return (
          <>
            {row.name}
            <div className="t-caption">
              {ORIGIN_DETAIL_LABEL[row.origin]}
              {row.note ? ` · ${row.note}` : ''}
            </div>
            {/*
              Цепочка «находка → предписание → работа» не должна прерываться:
              строка о выполненной обработке ведёт на само требование в блоке
              «Карантин» этой же страницы — там же стоит и результат повторного
              контроля, который эта строка сама по себе не показывает.
            */}
            {requirement ? (
              <div className="t-caption">
                <a href="#quarantine">{requirement}</a>
              </div>
            ) : null}
          </>
        );
      },
    },
    {
      key: 'origin',
      header: t(M.colOrigin),
      render: (row) => (
        <Badge tone="copper" icon={ORIGIN_ICON[row.origin]}>
          {ORIGIN_BADGE_LABEL[row.origin]}
        </Badge>
      ),
    },
  ];

  /*
   * Контура с таким адресом в срезе нет — фактические блоки не рисуются вовсе.
   * «Землепользование: —» и пустая карта читаются как факты о контуре, хотя
   * контура нет: это уже не пустое состояние, а выдуманное.
   */
  if (contours.state === 'ready' && !contour) {
    return (
      <Stack>
        <PageHeader level="contour" title={t(M.titleId, { id })} />
        <EmptyState title={t(M.notFoundTitle)} reason={t(M.notFoundReason, { id })} />
      </Stack>
    );
  }

  /*
   * Уровень фермера заканчивается его хозяйством. Адрес чужого контура открыть
   * можно — шаблон пути про принадлежность ничего не знает, — но паспорт здесь
   * НЕ рисуется: это чужие площадь, культура, работы и карантин. Отказ называется
   * словами, а не пустым экраном.
   */
  if (persona?.role === 'farmer' && contour && contour.farm_id !== persona.farmId) {
    return (
      <Stack>
        <PageHeader level="contour" title={t(M.titleNo, { no: contour.no })} />
        <EmptyState
          title={t(M.foreignTitle)}
          reason={t(M.foreignReason, { no: contour.no })}
          action={
            <Link className="btn btn--secondary focus-ring" to="/my/fields">
              {t(M.foreignBack)}
            </Link>
          }
        />
      </Stack>
    );
  }

  return (
    <Stack>
      <PageHeader
        level="contour"
        title={contour ? t(M.titleNo, { no: contour.no }) : t(M.titleId, { id })}
        purpose={contour || contours.state === 'loading' ? contourPurpose() : t(M.purposeMissing)}
        actions={
          contour ? (
            contour.geometry === 'none' ? (
              <Badge tone="outline">{t(M.noGeometry)}</Badge>
            ) : (
              <EvidenceMark
                kind={contour.geometry === 'candidate' ? 'candidate' : 'fact'}
                label={contour.geometry === 'candidate' ? t(M.geometryCandidate) : t(M.geometryAccepted)}
              />
            )
          ) : null
        }
      />

      <DataGate
        res={contours}
        what={t(M.gatePassport)}
        skeleton={
          <div data-guide="kpi">
            <Grid cols={4}>{Array.from({ length: 4 }, (_, i) => <SkeletonKpi key={i} label={t(M.skelPassport)} />)}</Grid>
          </div>
        }
      >
        {() =>
          contour ? (
            <ContourKpis
              area={{ value: ha(contour.area_ha), note: t(M.kpiAreaNote) }}
              category={{
                label: t(M.kpiCategory),
                value: contour.category in CATEGORY_MSG ? t(CATEGORY_MSG[contour.category as keyof typeof CATEGORY_MSG]) : contour.category,
                note: t(M.kpiCategoryNote),
              }}
              crop={{
                label: t(M.kpiCrop),
                value: cropName(contour.crop_2026),
                note: contour.crop_2026 ? t(M.kpiCropNote) : t(M.kpiCropMissing),
              }}
              winter={{
                label: t(M.kpiWinter),
                value: contour.crop_2025w ? cropName(contour.crop_2025w) : t(M.kpiWinterNone),
                note: t(M.kpiWinterNote),
              }}
            />
          ) : null
        }
      </DataGate>

      <LandUseCard
        farm={contour ? <Link to={`/farms/${contour.farm_id}`}>{farmName}</Link> : contours.state === 'loading' ? <Skeleton width={120} /> : '—'}
        caption={t(M.landUseCaption)}
      />

      <ContourWorksCard
        works={
          worksLoading ? (
            <SkeletonTable label={t(M.skelWorks)} rows={4} cols={5} />
          ) : worksAllMissing ? (
            <EmptyState
              compact
              title={t(M.worksMissingTitle)}
              reason={t(M.worksMissingReason)}
            />
          ) : (
            <ContourWorksTable rows={workRows} emptyReason={t(M.worksEmptyReason)} />
          )
        }
        extra={
          <DataGate
            res={extraBundle}
            what={t(M.gateExtra)}
            skeleton={<SkeletonTable label={t(M.skelExtra)} rows={2} cols={4} />}
          >
            {(rows) => (
              <DataTable
                columns={extraWorkColumns}
                rows={contourExtraWorks(rows, contour?.id ?? null)}
                getKey={(row) => row.id}
                denominator={M.extraUnit}
                empty={{
                  title: t(M.extraEmptyTitle),
                  reason: t(M.extraEmptyReason),
                }}
              />
            )}
          </DataGate>
        }
      />

      {/*
        Якорь `#quarantine`: строка добровольной работы «по предписанию» ведёт
        именно сюда. Экран, который показывает выполненную обработку и молчит о
        самом требовании и о результате повторного контроля, обрывает цепочку
        «находка → предписание → работа» ровно там, где она нужнее всего.
      */}
      <Card title={t(M.quarantineTitle)} id="quarantine">
        <DataGate res={quarantine} what={t(M.gateQuarantine)} skeleton={<SkeletonText lines={3} />}>
          {() => (
            <div data-guide="quarantine"><Stack tight>
              {status ? (
                status.status === 'red' ? (
                  <p className="t-body" style={{ color: 'var(--data-critical-ink)' }}>
                    {status.cause
                      ? t(M.redWithCause, { date: dateShort(status.since), cause: status.cause, b: (c) => <strong>{c}</strong> })
                      : t(M.redNoCause, { date: dateShort(status.since), b: (c) => <strong>{c}</strong> })}
                  </p>
                ) : status.status === 'watch' ? (
                  <p className="t-body">
                    {status.cause
                      ? t(M.watchWithCause, { date: dateShort(status.since), cause: status.cause })
                      : t(M.watchNoCause, { date: dateShort(status.since) })}
                  </p>
                ) : (
                  <p className="t-body">{t(M.greenRecorded, { date: dateShort(status.since) })}</p>
                )
              ) : (
                <p className="t-body">{t(M.greenDefault)}</p>
              )}

              <QuarantineFacts
                lines={[
                  ...inspections.map((insp) => ({
                    key: insp.id,
                    date: insp.date,
                    text:
                      insp.result === 'clean'
                        ? t(M.inspClean)
                        : insp.finding?.measure
                          ? t(M.inspFindingMeasure, { kind: insp.finding?.kind ?? t(M.inspKindFallback), name: insp.finding?.name ?? '—', measure: insp.finding.measure })
                          : t(M.inspFinding, { kind: insp.finding?.kind ?? t(M.inspKindFallback), name: insp.finding?.name ?? '—' }),
                  })),
                  ...(requirement
                    ? [
                        {
                          key: requirement.id,
                          date: requirement.issued_on,
                          text: requirement.measure
                            ? t(M.reqIssuedMeasure, { deadline: dateShort(requirement.deadline), measure: requirement.measure })
                            : t(M.reqIssued, { deadline: dateShort(requirement.deadline) }),
                        },
                      ]
                    : []),
                  ...(followup
                    ? [
                        {
                          key: followup.id,
                          date: followup.date,
                          text: t(M.followupLine, { result: FOLLOWUP_RESULT_LABEL[followup.result] }),
                        },
                      ]
                    : []),
                ]}
              />

              {requirement && !followup ? (
                <p className="t-caption">{t(M.noFollowup)}</p>
              ) : null}
            </Stack></div>
          )}
        </DataGate>
      </Card>

      <ContourMapCard contourId={contour?.id ?? null} crops={crops.data ?? undefined} />
    </Stack>
  );
}
